import { ServiceTemplateSchedulesViewColumn } from "@common/types/views/serviceTemplateSchedulesView";
import { WorkspaceTimezone } from "@common/workspaceTimezone";
import {
    createColumns,
    type DataGridColumn,
    type DataGridColumnOptions,
} from "@design-system/DataGrid";
import { serviceTemplateScheduleDisplayStatusLabel } from "../serviceTemplateScheduleDisplayStatus";
import { formatScheduleRecurrence } from "../serviceTemplateScheduleRecurrence";
import {
    serviceTemplateSchedulesViewConfig,
    type ServiceTemplateScheduleLeafRow,
    type ServiceTemplateScheduleRow,
} from "../serviceTemplateSchedulesViewConfig";
import {
    buildClientSummary,
    buildEntitySummary,
    buildScheduleActionsCell,
    buildTextSummary,
    type ServiceTemplateScheduleCallbacks,
} from "./scheduleListCells";
import {
    getClientInfo,
    getEstimatedTimeText,
    getEstimatedTimeTooltip,
    getNextServicePeriod,
    getPeriodLabel,
    getScheduleDisplayStatus,
    getStatusReason,
    type ScheduleListLookups,
    scheduleValue,
    toEngagementTeam,
} from "./scheduleListValues";

export type ScheduleListColumn = DataGridColumn<ServiceTemplateScheduleRow>;

/** The page-wide values the columns read; the table rebuilds them when one changes. */
export interface ScheduleListColumnInputs {
    lookups: ScheduleListLookups;
    callbacks: ServiceTemplateScheduleCallbacks;
    /** A pause or resume is in flight, so neither can start. */
    isUpdatingStatus: boolean;
}

type ScheduleRow = ServiceTemplateScheduleLeafRow;
type ScheduleDataColumn = Exclude<
    ServiceTemplateSchedulesViewColumn,
    ServiceTemplateSchedulesViewColumn.ACTIONS
>;
type ScheduleColumnConfig =
    (typeof serviceTemplateSchedulesViewConfig.columns)[ServiceTemplateSchedulesViewColumn];

interface ScheduleColumnArgs {
    id: ScheduleDataColumn;
    options: DataGridColumnOptions<ServiceTemplateScheduleRow>;
    lookups: ScheduleListLookups;
}

type ScheduleColumnBuilder = (args: ScheduleColumnArgs) => ScheduleListColumn;

const col = createColumns<ServiceTemplateScheduleRow>();

const textColumn =
    (read: (row: ScheduleRow) => string | null): ScheduleColumnBuilder =>
    ({ id, options }) =>
        col.text({ id, value: scheduleValue(read) }, options);

const dateColumn =
    (
        read: (row: ScheduleRow) => string | null | undefined,
    ): ScheduleColumnBuilder =>
    ({ id, options }) =>
        col.date({ id, value: scheduleValue(read) }, options);

/** Schedules and their groups; schedules are leaves. */
export const getScheduleListSubRows = (
    row: ServiceTemplateScheduleRow,
): ServiceTemplateScheduleRow[] | undefined =>
    row.rowType === "group" ? row.children : undefined;

const SCHEDULE_LIST_COLUMN_BUILDERS: Record<
    ScheduleDataColumn,
    ScheduleColumnBuilder
> = {
    [ServiceTemplateSchedulesViewColumn.TEMPLATE_NAME]: ({
        id,
        options,
        lookups,
    }) =>
        col.text(
            {
                id,
                value: (row) => lookups.templateNameById.get(row.templateId),
            },
            { ...options, tree: true, pin: "start" },
        ),
    [ServiceTemplateSchedulesViewColumn.CLIENT]: ({ id, options, lookups }) =>
        col.workspace(
            {
                id,
                value: scheduleValue((row) => getClientInfo({ row, lookups })),
            },
            { ...options, summary: buildClientSummary(lookups) },
        ),
    [ServiceTemplateSchedulesViewColumn.ENTITY]: ({ id, options, lookups }) =>
        col.entity(
            {
                id,
                value: scheduleValue((row) =>
                    lookups.entityInfoByUuid.get(row.entityUuid),
                ),
            },
            { ...options, summary: buildEntitySummary(lookups) },
        ),
    [ServiceTemplateSchedulesViewColumn.SCHEDULE]: textColumn(
        formatScheduleRecurrence,
    ),
    [ServiceTemplateSchedulesViewColumn.PERIOD]: ({ id, options }) =>
        col.text(
            { id, value: getPeriodLabel },
            { ...options, summary: buildTextSummary(getPeriodLabel) },
        ),
    [ServiceTemplateSchedulesViewColumn.STATUS]: ({ id, options, lookups }) =>
        col.text(
            {
                id,
                value: scheduleValue(
                    (row) =>
                        serviceTemplateScheduleDisplayStatusLabel[
                            getScheduleDisplayStatus({ row, lookups })
                        ],
                ),
            },
            {
                ...options,
                tooltip: scheduleValue((row) =>
                    getStatusReason({ row, lookups }),
                ),
            },
        ),
    [ServiceTemplateSchedulesViewColumn.NEXT_SERVICE_PERIOD]:
        textColumn(getNextServicePeriod),
    [ServiceTemplateSchedulesViewColumn.NEXT_APPLICATION_DATE]: dateColumn(
        (row) => row.nextApplicationDate,
    ),
    [ServiceTemplateSchedulesViewColumn.NEXT_WORK_START]: dateColumn(
        (row) => row.nextWorkStartDate,
    ),
    [ServiceTemplateSchedulesViewColumn.NEXT_WORK_END]: dateColumn(
        (row) => row.nextWorkEndDate,
    ),
    [ServiceTemplateSchedulesViewColumn.ENGAGEMENT_TEAM]: ({
        id,
        options,
        lookups,
    }) =>
        col.people(
            {
                id,
                value: scheduleValue((row) =>
                    toEngagementTeam({ row, lookups }),
                ),
            },
            { ...options, max: 3 },
        ),
    [ServiceTemplateSchedulesViewColumn.ESTIMATED_TIME]: ({
        id,
        options,
        lookups,
    }) => {
        const readText = (row: ServiceTemplateScheduleRow) =>
            getEstimatedTimeText({ row, lookups });
        return col.text(
            { id, value: readText },
            {
                ...options,
                summary: buildTextSummary(readText),
                tooltip: (row) => getEstimatedTimeTooltip({ row, lookups }),
            },
        );
    },
    [ServiceTemplateSchedulesViewColumn.CREATED_AT]: ({
        id,
        options,
        lookups,
    }) =>
        col.timestamp(
            {
                id,
                value: scheduleValue((row) =>
                    getClientInfo({ row, lookups })?.timezone
                        ? row.createdAt
                        : null,
                ),
            },
            {
                ...options,
                // A client that left the organization, or a backend that
                // predates the list's `timezone`, leaves no zone to read the
                // day in, so those rows show no value and never reach this.
                timeZone: (row) =>
                    (row.rowType === "schedule" &&
                        getClientInfo({ row, lookups })?.timezone) ||
                    WorkspaceTimezone.UTC,
            },
        ),
};

/** What every column takes from its config: header, widths, sorting, and hiding. */
const toColumnOptions = (
    config: ScheduleColumnConfig,
): DataGridColumnOptions<ServiceTemplateScheduleRow> => ({
    header: config.header,
    ariaLabel: config.name,
    size: config.minSize,
    minSize: config.minSize,
    maxSize: config.maxSize,
    fill: 1,
    fillUntilResized: true,
    align: config.align === "right" ? "end" : undefined,
    enableSorting: Boolean(config.sort),
    enableHiding: !config.immutable,
});

const isDataColumn = (
    id: ServiceTemplateSchedulesViewColumn,
): id is ScheduleDataColumn =>
    id !== ServiceTemplateSchedulesViewColumn.ACTIONS;

/**
 * The Schedules list's DataGrid columns in `serviceTemplateSchedulesViewConfig`
 * order, between a pinned select column and the pinned row actions, with the
 * config's ids so saved views apply as they are. Each starts at its config
 * minimum and shares leftover width up to its maximum; sortable ones sort
 * descending first, as ViewTable did.
 */
export const buildScheduleListColumns = ({
    lookups,
    callbacks,
    isUpdatingStatus,
}: ScheduleListColumnInputs): ScheduleListColumn[] => {
    const { columns } = serviceTemplateSchedulesViewConfig;
    const dataColumns = Object.values(ServiceTemplateSchedulesViewColumn)
        .filter(isDataColumn)
        .map((id) => ({
            ...SCHEDULE_LIST_COLUMN_BUILDERS[id]({
                id,
                options: toColumnOptions(columns[id]),
                lookups,
            }),
            sortDescFirst: columns[id].sort?.defaultOrder !== "asc",
        }));
    return [
        col.select({ pin: "start" }),
        ...dataColumns,
        col.actions({
            id: ServiceTemplateSchedulesViewColumn.ACTIONS,
            render: buildScheduleActionsCell({
                lookups,
                callbacks,
                isUpdatingStatus,
            }),
            width: columns[ServiceTemplateSchedulesViewColumn.ACTIONS].minSize,
            pin: "end",
        }),
    ];
};
